import { uniq } from 'lodash-es';

import { type ExpressionObj } from '@blaze-shared/elements-domain';
import { isDefined } from '@blaze-shared/shared-util';

const VARIABLES_REGEX = /@@(\w+)/g; //@@VARIABLE
const BRACKET_COLUMN_WITH_INPUT_NAME_REGEX = /\$\$(\w+)(?:\.\.)?(?:\["[^"]+"])+/g; // example: $$inputName["column1"]["column2"], $$inputName["column1"]
const DOT_COLUMN_WITH_INPUT_NAME_REGEX = /\$\$(\w+)(?:\.(colId:[a-f0-9]{24}_[a-f0-9]{24}|\w+))?/g; // example: $$inputName.column1, $$inputName, $$input1.colId:3cf5e6c43c852a139191df47_405ac41971793250fc29b628
const INPUT_NAME_REGEX = /\$\$(\w+)(?=\s|$)/g; // example: $$inputName
const BRACKET_PAIRS_REGEX = /\["([^"]+)"]/g; // example: ["column1"], ["column2"]
const COLUMN_REGEX = /\b(colId:[a-f0-9]{24}_[a-f0-9]{24})\b/g; // example: colId:5f7b1b7b7b7b7b7b7b7b7b7_5f7b1b7b7b7b7b7b7b7b7b
const TAG_ID_REGEX = /tagId:[a-f0-9]{24}(?![a-f0-9])/g; // example: tagId:5f7b1b7b7b7b7b7b7b7b7b7b
const PARAM_REGEX = /\{([a-zA-Z0-9]+)}/g; // example: {paramName}

type ExtractedColumnReference = {
  inputName?: string;
  columns: string[];
  sourceId?: string;
};

type ExtractedState = {
  variables: ExtractedColumnReference[];
  extractedInputsAndColumns: Set<string>;
  extractedColumns: Set<string>;
};

export function extractColumnsWithInputsFromString(
  inputString: string,
  sourceId?: string,
): ExtractedColumnReference[] {
  const state: ExtractedState = {
    variables: [],
    extractedInputsAndColumns: new Set(),
    extractedColumns: new Set(),
  };

  let match: RegExpExecArray | null;

  // Match bracket notation: $$inputName["column1"]["column2"]
  BRACKET_COLUMN_WITH_INPUT_NAME_REGEX.lastIndex = 0;
  while ((match = BRACKET_COLUMN_WITH_INPUT_NAME_REGEX.exec(inputString)) !== null) {
    const fullMatch = match[0];
    const inputName = match[1];
    const columns = extractBracketColumns(fullMatch);
    if (inputName && columns.length > 0) {
      processColumns(state, inputName, columns, sourceId);
    }
  }

  // Match dot notation: $$inputName.column1 or colId:...
  DOT_COLUMN_WITH_INPUT_NAME_REGEX.lastIndex = 0;
  while ((match = DOT_COLUMN_WITH_INPUT_NAME_REGEX.exec(inputString)) !== null) {
    const inputName = match[1];
    const column = match[2];
    if (inputName && column) {
      processColumns(state, inputName, [column], sourceId);
    }
  }

  COLUMN_REGEX.lastIndex = 0;
  while ((match = COLUMN_REGEX.exec(inputString)) !== null) {
    processColumnIdMatch(state, match[1], sourceId);
  }

  return state.variables;
}

export function extractNamedInputFromString(inputString: string): string[] {
  const namedInput: string[] = [];
  INPUT_NAME_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = INPUT_NAME_REGEX.exec(inputString)) !== null) {
    namedInput.push(match[1]);
  }
  return namedInput;
}

export function extractTagsFromString(inputString: string): string[] {
  const tags: string[] = [];
  TAG_ID_REGEX.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = TAG_ID_REGEX.exec(inputString)) !== null) {
    tags.push(match[0]);
  }

  return tags;
}

export function extractParamsFromString(inputString: string): string[] {
  const params: string[] = [];
  PARAM_REGEX.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = PARAM_REGEX.exec(inputString)) !== null) {
    params.push(match[1]);
  }

  return params;
}

export function extractVariablesFromString(inputString: string): string[] {
  const matches = inputString.match(VARIABLES_REGEX);
  return matches ? matches.map((match) => match.slice(2)) : [];
}

function extractBracketColumns(fullMatch: string): string[] {
  const bracketMatches = fullMatch.match(BRACKET_PAIRS_REGEX) || [];
  return bracketMatches
    .map((bracketMatch) => {
      const quoteMatch = /\["([^"]+)"]/.exec(bracketMatch);
      return quoteMatch ? quoteMatch[1] : '';
    })
    .filter(Boolean);
}

function processColumns(
  state: ExtractedState,
  inputName: string,
  columns: string[],
  sourceId?: string,
): void {
  const key = `${inputName}:${columns.join(':')}`;
  if (!state.extractedInputsAndColumns.has(key)) {
    state.variables.push({
      inputName,
      columns,
      sourceId,
    });
    state.extractedInputsAndColumns.add(key);
    columns.forEach((col) => state.extractedColumns.add(col));
  }
}

function processColumnIdMatch(state: ExtractedState, columnId: string, sourceId?: string): void {
  if (!state.extractedInputsAndColumns.has(columnId) && !state.extractedColumns.has(columnId)) {
    state.variables.push({ columns: [columnId], sourceId });
    state.extractedInputsAndColumns.add(columnId);
  }
}

export function extractInputsFromExpressions(
  items: { visibilityExpression?: ExpressionObj[] }[],
): string[] {
  const expressions = items
    .flatMap((item) => item.visibilityExpression)
    .flatMap((ve) => [ve?.columns, ve?.value])
    .filter(isDefined);
  const refs = expressions
    .flatMap((expr) => extractColumnsWithInputsFromString(expr))
    .map((ref) => ref.inputName)
    .filter(isDefined);
  return uniq(refs);
}
